import { useRef } from 'react';
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material';

import { MarkdownView, ResizableDrawer } from 'shared/ui';

import { ExplainTarget, getDiseaseExplainAsync } from '../../model/explain';

type Props = {
  target: ExplainTarget | null;
  onClose: () => void;
};

const ExplainBody = ({ target }: { target: ExplainTarget }) => {
  const { data, error, isLoading, isFetching, refetch } = getDiseaseExplainAsync(target);

  if (error) {
    const raw = error.response?.data?.message;
    const message = Array.isArray(raw) ? raw.join('; ') : raw ?? error.message;
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" disabled={isFetching} onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Failed to load explanation: {message}
      </Alert>
    );
  }

  if (!data || isLoading || isFetching) {
    return (
      <Box>
        <Skeleton variant="text" width="60%" height={36} />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="text" />
        <Skeleton variant="rectangular" height={80} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return <MarkdownView markdown={data.data.markdown} />;
};

export const DiseaseExplainDrawer = ({ target, onClose }: Props) => {
  // Последняя открытая цель: заголовок и тело остаются на месте, пока панель уезжает (target уже null).
  // После выхода Drawer размонтирует содержимое → запрос отменяется, а cacheTime 0 удаляет кэш.
  const shownRef = useRef(target);
  if (target) shownRef.current = target;
  const shown = shownRef.current;

  return (
    <ResizableDrawer
      open={!!target}
      onClose={onClose}
      titleId="disease-explain-title"
      storageKey="diseaseExplainDrawerWidth"
      title={
        shown && (
          <>
            {shown.diseaseName} ·{' '}
            <Typography component="span" variant="inherit" color="text.secondary">
              {shown.subjectName}
            </Typography>
          </>
        )
      }
    >
      {shown && <ExplainBody key={`${shown.kind}:${shown.subjectId}:${shown.diseaseId}`} target={shown} />}
    </ResizableDrawer>
  );
};
